import React, { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { BadgeCheck, ChevronRight, Clock, Loader2, Pencil, PenLine, Plus, Search } from "lucide-react";
import { api } from "../../api/client";
import { unwrapArray } from "../../lib/api-envelope";
import { fmtCurrency } from "../../lib/display-labels";
import { PageHeaderBand } from "../../components/layout/PageHeaderBand";
import { LoadFailureNotice } from "../../components/ui/LoadFailureNotice";
import { LOAD_FAILURE_QUERY_OPTIONS, queryFailureError, queryLoadFailure } from "../../lib/load-failure";
import { displayValue } from "../../features/fieldiq-handoff/contracts";
import { DhEmpty, DhPanel } from "../downhole/DownholeBits";
import NewRentalFieldTicketDialog from "../../components/rentals/NewRentalFieldTicketDialog";
import { LIST_CAP, STATUS, StatusBadge, TypeChip, periodLabel, skinOf, toTicket } from "./rental-field-ticket";

/**
 * The rental field ticket register, /rentals/tickets. Full width, the same
 * layout as casing's Field Tickets and downhole's Tool Tickets: band, status
 * pills, search, one register table; a row opens /rentals/tickets/:id.
 *
 * It was a split pane (a 384px list beside a detail panel) until #815. The
 * detail is now its own page, and the New Field Ticket dialog its own
 * component.
 *
 * COUNTS: the API has no ticket-count route. GET /tickets returns up to
 * LIST_CAP rows, newest first, with no total. Below the cap the list is the
 * whole register and a tally of it is the real figure; at the cap there may be
 * more, so every count — band and pills — reads "—" and the footer says why.
 */

const CHIPS: (string | null)[] = [null, "draft", "pending", "signed", "approved", "invoiced", "rejected"];

export default function RentalFieldTickets() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [newTicketOpen, setNewTicketOpen] = useState(false);

  const ticketsQuery = useQuery({
    queryKey: ["rentals", "fieldTickets"],
    queryFn: () =>
      api.rentals.tickets
        .list({ limit: String(LIST_CAP) })
        .then((r) => unwrapArray<Record<string, unknown>>(r.data).map(toTicket)),
    staleTime: 30_000,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
  const failed = queryLoadFailure(ticketsQuery) != null;
  const loading = ticketsQuery.isLoading && !failed;
  const loaded = !!ticketsQuery.data && !failed;
  const tickets = useMemo(() => ticketsQuery.data ?? [], [ticketsQuery.data]);
  const capped = tickets.length >= LIST_CAP;
  const countOf = (s: string | null): string =>
    !loaded || capped ? "—" : String(displayValue(tickets.filter((t) => s == null || t.status === s).length));

  const filtered = tickets.filter((t) => {
    const q = searchQuery.trim().toLowerCase();
    const matchesSearch =
      !q ||
      (t.ticket_number ?? "").toLowerCase().includes(q) ||
      (t.well_name ?? "").toLowerCase().includes(q) ||
      (t.customer_name ?? "").toLowerCase().includes(q);
    return matchesSearch && (!statusFilter || t.status === statusFilter);
  });

  const open = (id: string) => navigate(`/rentals/tickets/${encodeURIComponent(id)}`);
  const rowKeys = (id: string) => (event: React.KeyboardEvent) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      open(id);
    }
  };

  return (
    <div data-testid="rental-field-tickets-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow="Surface rentals"
        title="Field Tickets"
        onBack={() => navigate(-1)}
        backLabel="Back"
        actions={
          <button type="button" onClick={() => setNewTicketOpen(true)} className="fi-action fi-action--standard fi-action--primary">
            <Plus className="size-4" aria-hidden="true" />
            New Field Ticket
          </button>
        }
        stats={[
          { icon: <Pencil className="size-4" aria-hidden="true" />, label: "Drafts", sub: "Not yet sent", tone: "neutral", loading, value: countOf("draft") },
          { icon: <Clock className="size-4" aria-hidden="true" />, label: "Out for signature", sub: "Pending sign-off", tone: "info", loading, value: countOf("pending") },
          { icon: <PenLine className="size-4" aria-hidden="true" />, label: "To approve", sub: "Signed", tone: "accent", loading, value: countOf("signed") },
          { icon: <BadgeCheck className="size-4" aria-hidden="true" />, label: "Invoiced", sub: "Billed", tone: "success", loading, value: countOf("invoiced") },
        ]}
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
            <div className="scrollbar-hide flex flex-1 gap-2 overflow-x-auto" role="group" aria-label="Filter by status">
              {CHIPS.map((k) => {
                const active = statusFilter === k;
                const n = countOf(k);
                return (
                  <button
                    key={k ?? "all"}
                    type="button"
                    aria-pressed={active}
                    onClick={() => setStatusFilter(k)}
                    className={`inline-flex flex-shrink-0 items-center gap-1.5 whitespace-nowrap rounded-fi-pill px-4 py-1.5 text-xs font-extrabold transition ${
                      active ? "bg-fi-amber text-fi-amber-ink" : "border border-fieldiq-border bg-fieldiq-card text-fieldiq-muted hover:text-fieldiq-text"
                    }`}
                  >
                    {k == null ? "All" : STATUS[k].label}
                    {n !== "—" && <span className={`tabular-nums ${active ? "text-fi-amber-ink" : "text-fieldiq-muted"}`}>{n}</span>}
                  </button>
                );
              })}
            </div>
            <div className="relative lg:w-96">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-fieldiq-muted" aria-hidden="true" />
              <input
                type="text"
                placeholder="Search tickets, wells, customers..."
                aria-label="Search tickets"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full rounded-fi-control border border-fieldiq-border bg-fieldiq-card py-1.5 pl-10 pr-3 text-sm text-fieldiq-text placeholder:text-fieldiq-muted focus:border-fi-amber focus:outline-none"
              />
            </div>
          </div>

          <DhPanel
            eyebrow="Ticket register"
            title="Rental field tickets"
            testId="rental-ticket-register"
            footer={
              !loaded || tickets.length === 0
                ? undefined
                : capped
                  ? `Showing the ${tickets.length} most recent tickets. There may be more, so counts are not shown.`
                  : `Showing ${filtered.length} of ${tickets.length} tickets`
            }
          >
            {failed ? (
              <LoadFailureNotice error={queryFailureError(ticketsQuery)} subject="field tickets" />
            ) : loading ? (
              <div className="flex justify-center py-16">
                <Loader2 className="size-6 animate-spin text-fieldiq-muted" aria-label="Loading tickets" />
              </div>
            ) : filtered.length === 0 ? (
              <DhEmpty>{tickets.length === 0 ? "No field tickets yet." : "No tickets match these filters."}</DhEmpty>
            ) : (
              <>
                <div className="hidden overflow-x-auto md:block">
                  <table className="w-full min-w-[980px] text-left">
                    <thead className="border-b border-fieldiq-border bg-fieldiq-dark/40 text-[0.64rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">
                      <tr className="border-l-4 border-l-transparent">
                        <th scope="col" className="whitespace-nowrap px-4 py-3">Ticket #</th>
                        <th scope="col" className="px-4 py-3">Customer</th>
                        <th scope="col" className="px-4 py-3">Wellsite</th>
                        <th scope="col" className="px-4 py-3">Period</th>
                        <th scope="col" className="px-4 py-3 text-right">Total</th>
                        <th scope="col" className="px-4 py-3">Status</th>
                        <th scope="col" className="w-[44px] px-4 py-3" />
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.map((t) => (
                        <tr
                          key={t.id}
                          data-testid="rental-ticket-row"
                          role="button"
                          tabIndex={0}
                          onClick={() => open(t.id)}
                          onKeyDown={rowKeys(t.id)}
                          className={`cursor-pointer border-t border-l-4 border-fieldiq-border outline-none transition hover:bg-fieldiq-dark/50 focus-visible:bg-fi-amber/10 ${skinOf(t.status).rail}`}
                        >
                          <td className="whitespace-nowrap px-4 py-3">
                            <p className="font-mono text-sm font-extrabold text-fieldiq-accent-signal">{t.ticket_number ?? "—"}</p>
                            <div className="mt-1"><TypeChip type={t.ticket_type} /></div>
                          </td>
                          <td className="min-w-[12rem] px-4 py-3 text-sm text-fieldiq-text">{t.customer_name ?? "—"}</td>
                          <td className="min-w-[12rem] px-4 py-3 text-sm text-fieldiq-text">{t.well_name ?? "—"}</td>
                          <td className="whitespace-nowrap px-4 py-3 text-sm text-fieldiq-text">{periodLabel(t)}</td>
                          <td className="whitespace-nowrap px-4 py-3 text-right font-mono text-sm font-extrabold text-white">{fmtCurrency(t.total)}</td>
                          <td className="px-4 py-3"><StatusBadge status={t.status} /></td>
                          <td className="px-4 py-3"><ChevronRight className="size-4 text-fieldiq-muted" aria-hidden="true" /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="space-y-3 p-4 md:hidden">
                  {filtered.map((t) => (
                    <div
                      key={t.id}
                      data-testid="rental-ticket-card"
                      role="button"
                      tabIndex={0}
                      onClick={() => open(t.id)}
                      onKeyDown={rowKeys(t.id)}
                      className={`cursor-pointer rounded-fi-panel border border-l-4 border-fieldiq-border bg-fieldiq-card px-4 py-3 outline-none transition hover:border-fieldiq-muted focus-visible:border-fi-amber focus-visible:bg-fi-amber/10 ${skinOf(t.status).rail}`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <p className="font-mono text-sm font-extrabold text-fieldiq-accent-signal">{t.ticket_number ?? "—"}</p>
                        <StatusBadge status={t.status} />
                      </div>
                      <p className="mt-1 text-sm text-white">{t.customer_name ?? "—"}</p>
                      <p className="text-xs text-fieldiq-muted">{t.well_name ?? "—"}</p>
                      <div className="mt-2 flex items-center justify-between gap-3 text-xs text-fieldiq-muted">
                        <span>{periodLabel(t)}</span>
                        <span className="font-mono text-sm font-extrabold text-white">{fmtCurrency(t.total)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </DhPanel>
        </div>
      </section>

      <NewRentalFieldTicketDialog open={newTicketOpen} onOpenChange={setNewTicketOpen} />
    </div>
  );
}

